import clsx from 'clsx';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useState, useMemo } from 'react';

import { useIsMounted } from '../../hooks/use-is-mounted';

import { TeamLogoPlaceholder } from './team-logo-placeholder/team-logo-placeholder';

import './team-logo.scss';

const TRANSPARENT_PIXEL_URL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function getBaseImageSize(size) {
  if (size > 58 || !size) return 'base';
  if (size > 36) return 58;
  if (size > 18) return 36;
  return 18;
}

function getRetinaImageSize(size) {
  if (size > 29 || !size) return 'base';
  if (size > 18) return 58;
  if (size > 9) return 36;
  return 18;
}

function TeamLogoUI({ imageUrl, className: propsClassName, size, isHome }) {
  const isMounted = useIsMounted();
  const [isImageNotFound, setIsImageNotFound] = useState(false);

  const onError = useCallback(() => setIsImageNotFound(true), []);

  useEffect(() => {
    setIsImageNotFound(false);
  }, [imageUrl]);

  const className = useMemo(() => clsx('sb-TeamLogo', propsClassName), [propsClassName]);

  if (!isMounted) {
    return <img alt="" className={className} height={size} loading="lazy" src={TRANSPARENT_PIXEL_URL} width={size} />;
  }

  if (!imageUrl || isImageNotFound) {
    return <TeamLogoPlaceholder className={className} height={size} isHome={isHome} width={size} />;
  }

  if (!imageUrl.startsWith(process.env.CDN_URL)) {
    return (
      <img alt="" className={className} height={size} loading="lazy" src={imageUrl} width={size} onError={onError} />
    );
  }

  const folderUrl = imageUrl.replace('.png', '');
  const baseSize = getBaseImageSize(size);
  const retinaSize = getRetinaImageSize(size);

  return (
    <picture>
      <source
        data-test-id="team-logo-avif-source"
        srcSet={`${folderUrl}/${baseSize}.avif 1x, ${folderUrl}/${retinaSize}.avif 2x`}
        type="image/avif"
      />
      <source
        data-test-id="team-logo-webp-source"
        srcSet={`${folderUrl}/${baseSize}.webp 1x, ${folderUrl}/${retinaSize}.webp 2x`}
        type="image/webp"
      />
      <img
        alt=""
        className={className}
        height={size}
        loading="lazy"
        src={`${folderUrl}/${baseSize}.png`}
        srcSet={`${folderUrl}/${baseSize}.png 1x, ${folderUrl}/${retinaSize}.png 2x`}
        width={size}
        onError={onError}
      />
    </picture>
  );
}

TeamLogoUI.propTypes = {
  imageUrl: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.number,
  isHome: PropTypes.bool,
};

TeamLogoUI.defaultProps = {
  imageUrl: undefined,
  className: undefined,
  size: undefined,
  isHome: undefined,
};

export const TeamLogo = memo(TeamLogoUI);
